// components/admin/MaterialTypeManager.js - Componente dedicato per gestione Material Types
import React, { useState, useEffect, useCallback, useRef } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { materialManager } from "../../materialManager"
import { FaGripVertical } from "react-icons/fa"
import { addMaterial, uploadFile } from "../../api"

const MaterialTypeManager = ({
  isEditing,
  initialData,
  allMaterials,
  collections,
  crossCheckEnabled,
  fetchedMaterials,
  onSave,
  onCancel,
  onMaterialAdded, // Callback to refresh materials list after adding
}) => {
  // === FORM STATE ===
  const [typeFormData, setTypeFormData] = useState({
    technicalName: "",
    displayLabel: "",
    display: false,
    hidden: false,
    productFilterMode: crossCheckEnabled && fetchedMaterials.length > 0,
    lang: { it: "", en: "", fr: "", de: "" },
    materials: [],
    standardMaterials: [],
    optionalMaterials: [],
  })

  // === UI STATE ===
  const [selectedCollection, setSelectedCollection] = useState("all")
  const [availableTechnicalTypes, setAvailableTechnicalTypes] = useState([])
  const formRef = useRef(null)

  // === NEW MATERIAL STATE ===
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false)
  const [newMaterialData, setNewMaterialData] = useState({
    name: "",
    slug: "",
    collection: "",
    thumbnail: "",
    technicalType: "",
  })
  const [newMaterialThumbnailFile, setNewMaterialThumbnailFile] = useState(null)
  const [newMaterialThumbnailPreview, setNewMaterialThumbnailPreview] = useState(null)
  const [isAddingMaterial, setIsAddingMaterial] = useState(false)
  const [useColorPicker, setUseColorPicker] = useState(false)

  // === INITIALIZATION ===
  useEffect(() => {
    if (initialData) {
      setTypeFormData({
        technicalName: initialData.technicalName || "",
        displayLabel: initialData.displayLabel || "",
        display: Boolean(initialData.display),
        hidden: Boolean(initialData.hidden),
        productFilterMode: crossCheckEnabled && fetchedMaterials.length > 0,
        lang: initialData.lang || { it: "", en: "", fr: "", de: "" },
        materials: initialData.materials || [],
        standardMaterials: initialData.standardMaterials || [],
        optionalMaterials: initialData.optionalMaterials || [],
        // IMPORTANT: Preserve exact Emersya codes (with spaces) for products.json
        _exactStandardCodes: initialData._exactStandardCodes || [],
        _exactOptionalCodes: initialData._exactOptionalCodes || [],
      })
    } else {
      setTypeFormData({
        technicalName: "",
        displayLabel: "",
        display: false,
        hidden: false,
        productFilterMode: crossCheckEnabled && fetchedMaterials.length > 0,
        lang: { it: "", en: "", fr: "", de: "" },
        materials: [],
        standardMaterials: [],
        optionalMaterials: [],
        _exactStandardCodes: [],
        _exactOptionalCodes: [],
      })
    }
  }, [initialData, crossCheckEnabled, fetchedMaterials])

  // Populate technical types dropdown from cross-check
  useEffect(() => {
    if (crossCheckEnabled && fetchedMaterials.length > 0) {
      const typesWithMultipleMaterials = fetchedMaterials
        .filter((type) => type.variations && type.variations.length >= 2)
        .map((type) => type.technicalName)
        .sort()

      setAvailableTechnicalTypes(typesWithMultipleMaterials)
    } else {
      setAvailableTechnicalTypes([])
    }
  }, [crossCheckEnabled, fetchedMaterials])

  // === FORM VALIDATION ===
  const isFormValid = () => {
    return typeFormData.technicalName && typeFormData.displayLabel
  }

  // === FORM HANDLERS ===
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === "checkbox" ? checked : value

    setTypeFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }))
  }

  const handleLanguageChange = (lang, value) => {
    setTypeFormData((prev) => ({
      ...prev,
      lang: {
        ...prev.lang,
        [lang]: value,
      },
    }))
  }

  // === MATERIAL MANAGEMENT ===
  // Helper to find the EXACT Emersya name (with spaces) for a clean material name
  const findEmersyaExactName = (cleanMaterialName) => {
    if (!fetchedMaterials.length || !typeFormData.technicalName) {
      return cleanMaterialName // Fallback to clean name
    }

    const fetchedType = fetchedMaterials.find(
      (fm) => fm.technicalName.toLowerCase() === typeFormData.technicalName.toLowerCase()
    )

    if (!fetchedType?.variations?.length) {
      return cleanMaterialName
    }

    // Find the Emersya variation that matches this clean name (case-insensitive, trimmed)
    const cleanLower = cleanMaterialName.trim().toLowerCase()
    const matchingVariation = fetchedType.variations.find(
      (v) => v.name.trim().toLowerCase() === cleanLower
    )

    // Return the ORIGINAL Emersya name (with spaces if any), or fallback to clean name
    return matchingVariation ? matchingVariation.name : cleanMaterialName
  }

  const handleMaterialStatusChange = (materialName, status) => {
    setTypeFormData((prev) => {
      const newState = { ...prev }

      // Find the exact Emersya name (may have spaces) for products.json
      const exactEmersyaName = findEmersyaExactName(materialName)

      // Initialize exact code arrays if not present
      newState._exactStandardCodes = [...(prev._exactStandardCodes || [])]
      newState._exactOptionalCodes = [...(prev._exactOptionalCodes || [])]

      // Remove from both lists first (match by trimmed comparison)
      const materialIndex = newState.standardMaterials.indexOf(materialName)
      if (materialIndex > -1) {
        newState.standardMaterials.splice(materialIndex, 1)
        newState._exactStandardCodes.splice(materialIndex, 1)
      }

      const optionalIndex = newState.optionalMaterials.indexOf(materialName)
      if (optionalIndex > -1) {
        newState.optionalMaterials.splice(optionalIndex, 1)
        newState._exactOptionalCodes.splice(optionalIndex, 1)
      }

      // Add to appropriate list
      if (status === "standard") {
        newState.standardMaterials.push(materialName)
        newState._exactStandardCodes.push(exactEmersyaName)
      } else if (status === "optional") {
        newState.optionalMaterials.push(materialName)
        newState._exactOptionalCodes.push(exactEmersyaName)
      }

      // Update materials list
      if (status === "standard" || status === "optional") {
        if (!newState.materials.includes(materialName)) {
          newState.materials.push(materialName)
        }
      } else {
        newState.materials = newState.materials.filter(
          (m) => m !== materialName
        )
      }

      return newState
    })
  }

  const handleRemoveMaterial = (materialName) => {
    setTypeFormData((prev) => {
      // Find indices to remove from exact codes arrays
      const stdIndex = prev.standardMaterials.indexOf(materialName)
      const optIndex = prev.optionalMaterials.indexOf(materialName)

      const newExactStandard = [...(prev._exactStandardCodes || [])]
      const newExactOptional = [...(prev._exactOptionalCodes || [])]

      if (stdIndex > -1) {
        newExactStandard.splice(stdIndex, 1)
      }
      if (optIndex > -1) {
        newExactOptional.splice(optIndex, 1)
      }

      return {
        ...prev,
        materials: prev.materials.filter((name) => name !== materialName),
        standardMaterials: prev.standardMaterials.filter(
          (name) => name !== materialName
        ),
        optionalMaterials: prev.optionalMaterials.filter(
          (name) => name !== materialName
        ),
        _exactStandardCodes: newExactStandard,
        _exactOptionalCodes: newExactOptional,
      }
    })
  }

  const handleBulkAddMaterials = (
    materialsToAdd,
    asStandard = false,
    asOptional = false
  ) => {
    setTypeFormData((prev) => {
      const newMaterials = [...prev.materials]
      const newStandardMaterials = [...prev.standardMaterials]
      const newOptionalMaterials = [...prev.optionalMaterials]
      const newExactStandard = [...(prev._exactStandardCodes || [])]
      const newExactOptional = [...(prev._exactOptionalCodes || [])]

      materialsToAdd.forEach((materialName) => {
        // Find exact Emersya name (may have spaces)
        const exactEmersyaName = findEmersyaExactName(materialName)

        if (!newMaterials.includes(materialName)) {
          newMaterials.push(materialName)
        }

        if (asStandard && !newStandardMaterials.includes(materialName)) {
          newStandardMaterials.push(materialName)
          newExactStandard.push(exactEmersyaName)
          const optionalIndex = newOptionalMaterials.indexOf(materialName)
          if (optionalIndex > -1) {
            newOptionalMaterials.splice(optionalIndex, 1)
            newExactOptional.splice(optionalIndex, 1)
          }
        } else if (asOptional && !newOptionalMaterials.includes(materialName)) {
          newOptionalMaterials.push(materialName)
          newExactOptional.push(exactEmersyaName)
          const standardIndex = newStandardMaterials.indexOf(materialName)
          if (standardIndex > -1) {
            newStandardMaterials.splice(standardIndex, 1)
            newExactStandard.splice(standardIndex, 1)
          }
        }
      })

      return {
        ...prev,
        materials: newMaterials,
        standardMaterials: newStandardMaterials,
        optionalMaterials: newOptionalMaterials,
        _exactStandardCodes: newExactStandard,
        _exactOptionalCodes: newExactOptional,
      }
    })
  }

  // === DRAG AND DROP ===
  const handleMaterialsReorder = useCallback(
    (result, isStandard) => {
      if (!result.destination) return

      const materialsArray = isStandard
        ? typeFormData.standardMaterials
        : typeFormData.optionalMaterials
      const exactCodesArray = isStandard
        ? typeFormData._exactStandardCodes || []
        : typeFormData._exactOptionalCodes || []

      // Reorder both arrays in the same way
      const items = Array.from(materialsArray)
      const exactItems = Array.from(exactCodesArray)

      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      // Reorder exact codes in sync
      if (exactItems.length > result.source.index) {
        const [reorderedExact] = exactItems.splice(result.source.index, 1)
        exactItems.splice(result.destination.index, 0, reorderedExact)
      }

      setTypeFormData((prev) => ({
        ...prev,
        [isStandard ? "standardMaterials" : "optionalMaterials"]: items,
        [isStandard ? "_exactStandardCodes" : "_exactOptionalCodes"]: exactItems,
        materials: [
          ...prev.standardMaterials.filter((m) => m !== reorderedItem),
          ...prev.optionalMaterials.filter((m) => m !== reorderedItem),
          ...(isStandard ? items : prev.standardMaterials),
          ...(!isStandard ? items : prev.optionalMaterials),
        ].filter((v, i, a) => a.indexOf(v) === i), // Remove duplicates
      }))
    },
    [typeFormData]
  )

  // === MATERIAL FILTERING ===
  const getFilteredMaterials = () => {
    if (selectedCollection === "all") {
      return allMaterials
    }
    return allMaterials.filter(
      (material) => material.collection === selectedCollection
    )
  }

  const getFilteredMaterialsWithCrossCheck = () => {
    let filtered = getFilteredMaterials()

    if (typeFormData.productFilterMode && fetchedMaterials.length > 0) {
      const currentTechnicalName = typeFormData.technicalName
      if (currentTechnicalName) {
        const fetchedType = fetchedMaterials.find(
          (fm) =>
            fm.technicalName.toLowerCase() ===
            currentTechnicalName.toLowerCase()
        )

        if (fetchedType?.variations?.length > 0) {
          // Normalize Emersya slugs: trim and lowercase for comparison
          const fetchedSlugsNormalized = fetchedType.variations.map((v) =>
            v.name.trim().toLowerCase()
          )
          const uniqueMaterials = new Map()

          filtered.forEach((material) => {
            const materialSlug = (material.slug || material.name || "").trim().toLowerCase()
            const materialName = (material.name || "").trim().toLowerCase()

            // Match against both slug and name (case-insensitive, trimmed)
            const matchesFetchedSlug = fetchedSlugsNormalized.some(
              (fetchedSlug) =>
                fetchedSlug === materialSlug ||
                fetchedSlug === materialName
            )

            if (matchesFetchedSlug) {
              const uniqueKey = materialSlug
              if (!uniqueMaterials.has(uniqueKey)) {
                uniqueMaterials.set(uniqueKey, material)
              }
            }
          })

          filtered = Array.from(uniqueMaterials.values())
        } else {
          filtered = []
        }
      }
    }

    return filtered
  }

  const getMaterialCrossCheckStatus = (materialName, technicalName = null) => {
    const typeToCheck = technicalName || typeFormData.technicalName

    if (!crossCheckEnabled || !typeToCheck || fetchedMaterials.length === 0) {
      return "normal"
    }

    const fetchedType = fetchedMaterials.find(
      (fm) => fm.technicalName.toLowerCase() === typeToCheck.toLowerCase()
    )

    if (!fetchedType?.variations?.length) {
      return "not-in-product"
    }

    // Normalize for comparison: trim and lowercase
    const fetchedNamesNormalized = fetchedType.variations.map((v) =>
      v.name.trim().toLowerCase()
    )
    const materialNameNormalized = (materialName || "").trim().toLowerCase()

    const materialExists = fetchedNamesNormalized.some(
      (fetchedName) => fetchedName === materialNameNormalized
    )

    return materialExists ? "exists" : "not-in-product"
  }

  // === HELPER FUNCTIONS ===
  const getImageUrl = (thumbnail) => {
    if (thumbnail?.startsWith("#")) return thumbnail
    if (
      thumbnail &&
      (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
    ) {
      return thumbnail
    }
    if (thumbnail) {
      const cleanPath = thumbnail.replace(/^\.\//, "")
      return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`
    }
    return "#"
  }

  // === MISSING MATERIALS DETECTION ===
  const getMissingMaterials = () => {
    if (!crossCheckEnabled || !typeFormData.technicalName || fetchedMaterials.length === 0) {
      return []
    }

    const currentTechnicalName = typeFormData.technicalName
    const fetchedType = fetchedMaterials.find(
      (fm) => fm.technicalName.toLowerCase() === currentTechnicalName.toLowerCase()
    )

    if (!fetchedType?.variations?.length) {
      return []
    }

    // Get all material slugs/names from materials.json (trimmed for comparison)
    const existingMaterialSlugs = new Set(
      allMaterials.map((m) => (m.slug?.trim() || m.name?.trim()).toLowerCase())
    )
    const existingMaterialNames = new Set(
      allMaterials.map((m) => m.name?.trim().toLowerCase())
    )

    // Find materials in Emersya that don't exist in materials.json
    // Note: Emersya often has spacing issues like " V4" or "V80 "
    const missing = fetchedType.variations.filter((variation) => {
      const variationName = variation.name.trim().toLowerCase()
      return !existingMaterialSlugs.has(variationName) && !existingMaterialNames.has(variationName)
    })

    // Add hasSpacingIssue flag to help UI show warnings
    return missing.map((variation) => ({
      ...variation,
      hasSpacingIssue: variation.name !== variation.name.trim(),
      cleanName: variation.name.trim(),
    }))
  }

  // === NEW MATERIAL HANDLERS ===
  const handleOpenAddMaterial = (emersyaMaterialName, originalEmersyaName = null) => {
    // emersyaMaterialName = clean name for materials.json
    // originalEmersyaName = original name from Emersya (may have spaces) for products.json
    const cleanName = emersyaMaterialName.trim()
    setNewMaterialData({
      name: cleanName,
      slug: cleanName,
      collection: collections[0] || "",
      thumbnail: "",
      technicalType: typeFormData.technicalName,
      // IMPORTANT: Store original Emersya name for products.json (for setMaterials API)
      originalEmersyaName: originalEmersyaName || cleanName,
    })
    setNewMaterialThumbnailFile(null)
    setNewMaterialThumbnailPreview(null)
    setUseColorPicker(false)
    setShowAddMaterialModal(true)
  }

  const handleNewMaterialThumbnailChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setNewMaterialThumbnailFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setNewMaterialThumbnailPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSaveNewMaterial = async () => {
    if (!newMaterialData.name || !newMaterialData.slug) {
      alert("Name and Slug are required")
      return
    }

    setIsAddingMaterial(true)

    try {
      let thumbnailUrl = newMaterialData.thumbnail

      // Upload thumbnail file if provided
      if (newMaterialThumbnailFile) {
        const uploadResult = await uploadFile(newMaterialThumbnailFile)
        thumbnailUrl = uploadResult.url
      }

      // Create the new material in materials.json (with CLEAN names for UI/matching)
      const materialToAdd = {
        name: newMaterialData.name.trim(),
        slug: newMaterialData.slug.trim(),
        collection: newMaterialData.collection,
        thumbnail: thumbnailUrl,
        technicalTypes: [newMaterialData.technicalType],
        type: newMaterialData.technicalType.toLowerCase(),
      }

      await addMaterial(materialToAdd)

      // Notify parent to refresh materials list
      if (onMaterialAdded) {
        await onMaterialAdded()
      }

      // Auto-add to the current type as optional
      // Use CLEAN name for UI display (materialName)
      // but store ORIGINAL Emersya name in _exactOptionalCodes for products.json
      const cleanName = newMaterialData.name.trim()
      const originalName = newMaterialData.originalEmersyaName || cleanName

      // Add to optionalMaterials array (clean name for UI)
      setTypeFormData((prev) => {
        const newOptional = [...prev.optionalMaterials]
        if (!newOptional.includes(cleanName)) {
          newOptional.push(cleanName)
        }

        // Also update _exactOptionalCodes to preserve original Emersya spacing
        const newExactOptional = [...(prev._exactOptionalCodes || [])]
        if (!newExactOptional.some(code => code.trim() === originalName.trim())) {
          newExactOptional.push(originalName)
        }

        return {
          ...prev,
          materials: [...prev.standardMaterials, ...newOptional],
          optionalMaterials: newOptional,
          _exactOptionalCodes: newExactOptional,
        }
      })

      setShowAddMaterialModal(false)
      alert(`Material "${newMaterialData.name}" added successfully!\n\nNote: Saved to materials.json as "${cleanName}" and to product config as "${originalName}"`)
    } catch (error) {
      console.error("Failed to add material:", error)
      alert(`Failed to add material: ${error.message}`)
    } finally {
      setIsAddingMaterial(false)
    }
  }

  // === FORM SUBMISSION ===
  const handleSubmit = (e) => {
    e.preventDefault()
    if (!isFormValid()) {
      alert("Technical name and display label are required")
      return
    }
    onSave(typeFormData)
  }

  // === RENDER ===
  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-6xl ay-max-h-screen ay-overflow-hidden ay-flex ay-flex-col">
        {/* Header fisso sempre visibile */}
        <div className="ay-flex ay-items-center ay-justify-between ay-mb-4 ay-flex-shrink-0">
          <h3 className="ay-text-lg ay-font-bold">
            {isEditing ? "Edit" : "Add"} Material Type
          </h3>

          {/* Bottoni azione sempre visibili */}
          <div className="ay-flex ay-gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              className={`ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-text-white ${
                isFormValid()
                  ? "ay-bg-blue-500 ay-hover:ay-bg-blue-600"
                  : "ay-bg-gray-400 ay-cursor-not-allowed"
              }`}
              disabled={!isFormValid()}
            >
              {isEditing ? "Update" : "Add"} Type
            </button>
          </div>
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="ay-flex ay-flex-col ay-flex-grow ay-overflow-hidden"
        >
          {/* Controlli obbligatori sempre visibili */}
          <div className="ay-flex-shrink-0 ay-mb-4 ay-pb-4 ay-border-b ay-border-gray-200">
            {/* Basic Information */}
            <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4 ay-mb-4">
              <div>
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Technical Name (Configurable Material in Emersya) *
                </label>
                {/* Technical Name Dropdown + Manual Input */}
                {availableTechnicalTypes.length > 0 ? (
                  <div className="ay-space-y-2">
                    <select
                      name="technicalName"
                      value={typeFormData.technicalName}
                      onChange={handleFormChange}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    >
                      <option value="">Select from product materials...</option>
                      {availableTechnicalTypes.map((typeName) => (
                        <option key={typeName} value={typeName}>
                          {typeName} (
                          {fetchedMaterials.find(
                            (f) => f.technicalName === typeName
                          )?.variations?.length || 0}{" "}
                          materials)
                        </option>
                      ))}
                    </select>
                    <div className="ay-flex ay-items-center ay-text-xs ay-text-gray-500">
                      <span>Or type manually:</span>
                    </div>
                    <input
                      type="text"
                      name="technicalName"
                      value={typeFormData.technicalName}
                      onChange={handleFormChange}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                      placeholder="Enter custom technical name..."
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    name="technicalName"
                    value={typeFormData.technicalName}
                    onChange={handleFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    required
                  />
                )}
                <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                  Must match the technical name used in the 3D model.
                  {crossCheckEnabled && fetchedMaterials.length > 0 && (
                    <span className="ay-text-blue-600">
                      {" "}
                      Available types are auto-detected from product.
                    </span>
                  )}
                </p>
              </div>

              <div>
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Display Label (Fallback)
                </label>
                <input
                  type="text"
                  name="displayLabel"
                  value={typeFormData.displayLabel}
                  onChange={handleFormChange}
                  className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                />
                <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                  Default label if translations are not available.
                </p>
              </div>
            </div>

            {/* Display Settings sempre visibili */}
            <div className="ay-mb-4 ay-p-3 ay-border ay-rounded-lg ay-bg-gray-50">
              <h4 className="ay-text-sm ay-font-medium ay-mb-3">
                Display Settings
              </h4>
              <div className="ay-flex ay-gap-6">
                <label
                  className="ay-flex ay-items-center ay-gap-2"
                  title="Show only configured materials, ignore others available in viewer"
                >
                  <input
                    type="checkbox"
                    name="display"
                    checked={typeFormData.display}
                    onChange={handleFormChange}
                    className="ay-rounded"
                  />
                  <span className="ay-text-sm">
                    <strong>Display Filter</strong>
                  </span>
                </label>
                <label
                  className="ay-flex ay-items-center ay-gap-2"
                  title="Hide this material type accordion in configurator"
                >
                  <input
                    type="checkbox"
                    name="hidden"
                    checked={typeFormData.hidden}
                    onChange={handleFormChange}
                    className="ay-rounded"
                  />
                  <span className="ay-text-sm">
                    <strong>Hidden</strong>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Contenuto scrollabile */}
          <div className="ay-flex-grow ay-overflow-auto ay-pr-2">
            {/* Label Translations */}
            <div className="ay-mb-6">
              <h4 className="ay-text-md ay-font-medium ay-mb-2">
                Label Translations
              </h4>
              <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-4 ay-gap-4">
                {["it", "en", "fr", "de"].map((lang) => (
                  <div key={lang}>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      {lang.toUpperCase()}
                    </label>
                    <input
                      type="text"
                      value={typeFormData.lang?.[lang] || ""}
                      onChange={(e) =>
                        handleLanguageChange(lang, e.target.value)
                      }
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm"
                      placeholder={`Label in ${lang.toUpperCase()}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Material Selection */}
            <div className="ay-mb-6 ay-select-none">
              <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                <h4 className="ay-text-md ay-font-medium ay-flex ay-items-center ay-gap-2">
                  Material Selection
                  {typeFormData.productFilterMode && (
                    <span className="ay-text-xs ay-bg-blue-100 ay-text-blue-700 ay-px-2 ay-py-1 ay-rounded">
                      Product Materials Only
                    </span>
                  )}
                </h4>
                <div className="ay-flex ay-items-center ay-gap-4">
                  {crossCheckEnabled && fetchedMaterials.length > 0 && (
                    <label className="ay-flex ay-items-center ay-gap-2 ay-text-sm ay-font-medium">
                      <input
                        type="checkbox"
                        name="productFilterMode"
                        checked={typeFormData.productFilterMode}
                        onChange={handleFormChange}
                        className="ay-rounded"
                      />
                      <span>Show only product materials</span>
                    </label>
                  )}
                  <div className="ay-flex ay-items-center ay-gap-2">
                    <label className="ay-text-sm ay-text-gray-700">
                      Filter by Collection:
                    </label>
                    <select
                      value={selectedCollection}
                      onChange={(e) => setSelectedCollection(e.target.value)}
                      disabled={typeFormData.productFilterMode}
                      className="ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500 ay-disabled:ay-bg-gray-100"
                    >
                      <option value="all">All Collections</option>
                      {collections.map((collection) => (
                        <option key={collection} value={collection}>
                          {collection}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              <div className="ay-mb-3 ay-bg-gray-50 ay-p-3 ay-rounded-md ay-flex ay-flex-wrap ay-gap-2">
                <button
                  type="button"
                  onClick={() =>
                    handleBulkAddMaterials(
                      getFilteredMaterialsWithCrossCheck().map((m) => m.name)
                    )
                  }
                  className="ay-px-3 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add All Displayed
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleBulkAddMaterials(
                      getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                      true,
                      false
                    )
                  }
                  className="ay-px-3 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add All as Standard
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleBulkAddMaterials(
                      getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                      false,
                      true
                    )
                  }
                  className="ay-px-3 ay-py-1 ay-bg-yellow-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add All as Optional
                </button>
              </div>

              {/* Material Controls */}
              <div className="ay-flex ay-justify-between ay-mb-3 ay-bg-gray-50 ay-p-2 ay-rounded">
                <div className="ay-flex ay-items-center ay-gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleBulkAddMaterials(
                        getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                        false,
                        true
                      )
                    }}
                    className="ay-px-2 ay-py-1 ay-bg-yellow-400 ay-text-white ay-rounded ay-text-xs"
                  >
                    Select All as Optional
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleBulkAddMaterials(
                        getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                        true,
                        false
                      )
                    }}
                    className="ay-px-2 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-xs"
                  >
                    Select All as Standard
                  </button>
                </div>
                <span className="ay-text-xs ay-text-gray-500">
                  Click = Optional | Shift+Click = Standard | Click again =
                  Remove
                </span>
              </div>

              {/* Materials Grid */}
              <div className="ay-max-h-96 ay-overflow-y-auto ay-space-y-4 ay-p-2 ay-bg-gray-50 ay-rounded-md">
                {getFilteredMaterialsWithCrossCheck().length > 0 ? (
                  (() => {
                    const materialsByCollection =
                      getFilteredMaterialsWithCrossCheck().reduce(
                        (acc, material) => {
                          const collection =
                            material.collection || "Uncategorized"
                          if (!acc[collection]) acc[collection] = []
                          acc[collection].push(material)
                          return acc
                        },
                        {}
                      )
                    const sortedCollections = Object.keys(
                      materialsByCollection
                    ).sort()

                    return sortedCollections.map((collection) => (
                      <div key={collection}>
                        <h6 className="ay-font-medium ay-text-sm ay-text-gray-600 ay-mb-2 ay-px-2">
                          {collection}
                        </h6>
                        <div className="ay-grid ay-grid-cols-2 sm:ay-grid-cols-3 md:ay-grid-cols-4 lg:ay-grid-cols-8 ay-gap-3">
                          {materialsByCollection[collection]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((material, materialIndex) => {
                              const isStandard =
                                typeFormData.standardMaterials.some(
                                  (stdMat) => stdMat === material.name
                                )
                              const isOptional =
                                typeFormData.optionalMaterials.some(
                                  (optMat) => optMat === material.name
                                )
                              const crossCheckStatus =
                                getMaterialCrossCheckStatus(
                                  material.name,
                                  typeFormData.technicalName
                                )

                              let statusClass = "ay-border-gray-300"
                              if (isStandard) {
                                statusClass =
                                  "ay-border-green-500 ay-ring-2 ay-ring-green-200"
                              } else if (isOptional) {
                                statusClass =
                                  "ay-border-yellow-500 ay-ring-2 ay-ring-yellow-200"
                              }

                              let statusIndicator = null
                              if (isStandard) {
                                statusIndicator = (
                                  <div className="ay-absolute ay-top-1 ay-right-1 ay-w-6 ay-h-6 ay-bg-green-500 ay-text-white ay-rounded-full ay-flex ay-items-center ay-justify-center ay-text-xs ay-font-bold">
                                    S
                                  </div>
                                )
                              } else if (isOptional) {
                                statusIndicator = (
                                  <div className="ay-absolute ay-top-1 ay-right-1 ay-w-6 ay-h-6 ay-bg-yellow-500 ay-text-white ay-rounded-full ay-flex ay-items-center ay-justify-center ay-text-xs ay-font-bold">
                                    O
                                  </div>
                                )
                              }

                              const crossCheckClass =
                                crossCheckStatus === "not-in-product"
                                  ? "ay-opacity-50"
                                  : ""

                              return (
                                <div
                                  key={`${collection}-${
                                    material.id ||
                                    material.slug ||
                                    material.name
                                  }-${materialIndex}`}
                                  className={`ay-border ay-rounded-lg ay-p-2 ay-cursor-pointer ay-transition-all ay-bg-white ay-shadow-sm hover:ay-shadow-md ay-relative ${statusClass} ${crossCheckClass}`}
                                  onClick={(e) => {
                                    if (e.shiftKey) {
                                      handleMaterialStatusChange(
                                        material.name,
                                        isStandard ? "none" : "standard"
                                      )
                                    } else {
                                      handleMaterialStatusChange(
                                        material.name,
                                        isOptional ? "none" : "optional"
                                      )
                                    }
                                  }}
                                >
                                  {/* Status Indicator */}
                                  {statusIndicator}

                                  {/* Cross Check Indicator */}
                                  {crossCheckEnabled && (
                                    <div
                                      className={`ay-absolute ay-bottom-1 ay-right-1 ay-text-xs ay-px-1 ay-rounded ay-font-bold ${
                                        crossCheckStatus === "exists"
                                          ? "ay-bg-green-600 ay-text-white"
                                          : crossCheckStatus ===
                                            "not-in-product"
                                          ? "ay-bg-red-600 ay-text-white"
                                          : "ay-bg-gray-400 ay-text-white"
                                      }`}
                                      title={
                                        crossCheckStatus === "exists"
                                          ? "Exists in product"
                                          : crossCheckStatus ===
                                            "not-in-product"
                                          ? "Not in product"
                                          : "No cross-check data"
                                      }
                                    >
                                      {crossCheckStatus === "exists"
                                        ? "✓"
                                        : crossCheckStatus === "not-in-product"
                                        ? "✗"
                                        : "?"}
                                    </div>
                                  )}

                                  <div className="ay-flex ay-flex-col ay-items-center ay-gap-2">
                                    {material.thumbnail ? (
                                      material.thumbnail.startsWith("#") ? (
                                        <div
                                          className="ay-w-10 ay-h-10 ay-rounded-full ay-border"
                                          style={{
                                            backgroundColor: material.thumbnail,
                                          }}
                                        ></div>
                                      ) : (
                                        <img
                                          src={getImageUrl(material.thumbnail)}
                                          alt={material.name}
                                          className="ay-w-10 ay-h-10 ay-rounded-full ay-object-cover ay-border"
                                        />
                                      )
                                    ) : (
                                      <div className="ay-w-10 ay-h-10 ay-bg-gray-200 ay-rounded-full ay-border"></div>
                                    )}

                                    <div className="ay-text-xs ay-text-center ay-w-full">
                                      <p
                                        className="ay-truncate ay-font-medium"
                                        title={material.name}
                                      >
                                        {material.name}
                                      </p>
                                      {material.slug &&
                                        material.slug !== material.name && (
                                          <p
                                            className="ay-text-[10px] ay-text-gray-500 ay-italic ay-truncate"
                                            title={`Slug: ${material.slug}`}
                                          >
                                            {material.slug}
                                          </p>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    ))
                  })()
                ) : (
                  <div className="ay-text-center ay-py-8 ay-text-gray-500">
                    {typeFormData.productFilterMode
                      ? `No materials found in the selected product for technical type "${typeFormData.technicalName}".`
                      : "No materials found."}
                    {typeFormData.productFilterMode && (
                      <div className="ay-mt-2">
                        <button
                          type="button"
                          onClick={() =>
                            setTypeFormData((prev) => ({
                              ...prev,
                              productFilterMode: false,
                            }))
                          }
                          className="ay-text-blue-500 ay-underline ay-text-sm"
                        >
                          Show all materials instead
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Materials Summary with Drag&Drop */}
              <div className="ay-border ay-border-gray-200 ay-rounded-md ay-p-3 ay-mt-4">
                <h5 className="ay-text-sm ay-font-medium ay-mb-4">
                  Selected Materials Summary
                </h5>

                {/* Standard Materials */}
                {typeFormData.standardMaterials.length > 0 && (
                  <div className="ay-mb-4">
                    <h6 className="ay-text-xs ay-font-medium ay-text-green-700 ay-mb-2 ay-flex ay-items-center ay-gap-2">
                      <div className="ay-w-3 ay-h-3 ay-bg-green-500 ay-rounded-full"></div>
                      Standard Materials (
                      {typeFormData.standardMaterials.length})
                    </h6>

                    <DragDropContext
                      onDragEnd={(result) =>
                        handleMaterialsReorder(result, true)
                      }
                    >
                      <Droppable droppableId="standard-materials">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="ay-space-y-1"
                          >
                            {typeFormData.standardMaterials.map(
                              (materialName, index) => {
                                const materialDetails = allMaterials.find(
                                  (m) =>
                                    m.name === materialName ||
                                    m.slug === materialName
                                )

                                const crossCheckStatus =
                                  getMaterialCrossCheckStatus(
                                    materialName,
                                    typeFormData.technicalName
                                  )
                                const crossCheckIcon = crossCheckEnabled
                                  ? crossCheckStatus === "exists"
                                    ? " ✓"
                                    : crossCheckStatus === "not-in-product"
                                    ? " ✗"
                                    : " ?"
                                  : ""

                                return (
                                  <Draggable
                                    key={materialName}
                                    draggableId={`std-${materialName}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`ay-flex ay-items-center ay-gap-2 ay-px-2 ay-py-1 ay-bg-green-50 ay-border ay-border-green-200 ay-rounded-md ay-text-xs ${
                                          snapshot.isDragging
                                            ? "ay-shadow-lg ay-bg-green-100"
                                            : ""
                                        } ${
                                          crossCheckStatus === "not-in-product"
                                            ? "ay-opacity-60"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          {...provided.dragHandleProps}
                                          className="ay-cursor-move ay-text-gray-400 ay-hover:ay-text-gray-600"
                                        >
                                          <FaGripVertical size={12} />
                                        </div>

                                        {/* Mini thumbnail */}
                                        <div className="ay-w-4 ay-h-4 ay-rounded-full ay-border ay-border-gray-300 ay-overflow-hidden ay-flex-shrink-0">
                                          {materialDetails?.thumbnail ? (
                                            materialDetails.thumbnail.startsWith(
                                              "#"
                                            ) ? (
                                              <div
                                                className="ay-w-full ay-h-full"
                                                style={{
                                                  backgroundColor:
                                                    materialDetails.thumbnail,
                                                }}
                                              />
                                            ) : (
                                              <img
                                                src={getImageUrl(
                                                  materialDetails.thumbnail
                                                )}
                                                alt={materialName}
                                                className="ay-w-full ay-h-full ay-object-cover"
                                              />
                                            )
                                          ) : (
                                            <div className="ay-w-full ay-h-full ay-bg-gray-200" />
                                          )}
                                        </div>

                                        <span className="ay-flex-grow ay-text-green-800">
                                          {materialName}
                                          {crossCheckIcon}
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveMaterial(materialName)
                                          }
                                          className="ay-text-gray-500 ay-hover:ay-text-red-600 ay-ml-1"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              }
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}

                {/* Optional Materials */}
                {typeFormData.optionalMaterials.length > 0 && (
                  <div className="ay-mb-4">
                    <h6 className="ay-text-xs ay-font-medium ay-text-yellow-700 ay-mb-2 ay-flex ay-items-center ay-gap-2">
                      <div className="ay-w-3 ay-h-3 ay-bg-yellow-500 ay-rounded-full"></div>
                      Optional Materials (
                      {typeFormData.optionalMaterials.length})
                    </h6>

                    <DragDropContext
                      onDragEnd={(result) =>
                        handleMaterialsReorder(result, false)
                      }
                    >
                      <Droppable droppableId="optional-materials">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="ay-space-y-1"
                          >
                            {typeFormData.optionalMaterials.map(
                              (materialName, index) => {
                                const materialDetails = allMaterials.find(
                                  (m) =>
                                    m.name === materialName ||
                                    m.slug === materialName
                                )

                                const crossCheckStatus =
                                  getMaterialCrossCheckStatus(
                                    materialName,
                                    typeFormData.technicalName
                                  )
                                const crossCheckIcon = crossCheckEnabled
                                  ? crossCheckStatus === "exists"
                                    ? " ✓"
                                    : crossCheckStatus === "not-in-product"
                                    ? " ✗"
                                    : " ?"
                                  : ""

                                return (
                                  <Draggable
                                    key={materialName}
                                    draggableId={`opt-${materialName}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`ay-flex ay-items-center ay-gap-2 ay-px-2 ay-py-1 ay-bg-yellow-50 ay-border ay-border-yellow-200 ay-rounded-md ay-text-xs ${
                                          snapshot.isDragging
                                            ? "ay-shadow-lg ay-bg-yellow-100"
                                            : ""
                                        } ${
                                          crossCheckStatus === "not-in-product"
                                            ? "ay-opacity-60"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          {...provided.dragHandleProps}
                                          className="ay-cursor-move ay-text-gray-400 ay-hover:ay-text-gray-600"
                                        >
                                          <FaGripVertical size={12} />
                                        </div>

                                        {/* Mini thumbnail */}
                                        <div className="ay-w-4 ay-h-4 ay-rounded-full ay-border ay-border-gray-300 ay-overflow-hidden ay-flex-shrink-0">
                                          {materialDetails?.thumbnail ? (
                                            materialDetails.thumbnail.startsWith(
                                              "#"
                                            ) ? (
                                              <div
                                                className="ay-w-full ay-h-full"
                                                style={{
                                                  backgroundColor:
                                                    materialDetails.thumbnail,
                                                }}
                                              />
                                            ) : (
                                              <img
                                                src={getImageUrl(
                                                  materialDetails.thumbnail
                                                )}
                                                alt={materialName}
                                                className="ay-w-full ay-h-full ay-object-cover"
                                              />
                                            )
                                          ) : (
                                            <div className="ay-w-full ay-h-full ay-bg-gray-200" />
                                          )}
                                        </div>

                                        <span className="ay-flex-grow ay-text-yellow-800">
                                          {materialName}
                                          {crossCheckIcon}
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveMaterial(materialName)
                                          }
                                          className="ay-text-gray-500 ay-hover:ay-text-red-600 ay-ml-1"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              }
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}

                {/* Empty state */}
                {typeFormData.materials.length === 0 && (
                  <div className="ay-text-center ay-py-4">
                    <p className="ay-text-sm ay-text-gray-500">
                      No materials selected yet.
                    </p>
                    <p className="ay-text-xs ay-text-gray-400 ay-mt-1">
                      Select materials from the grid above to see them organized
                      here.
                    </p>
                  </div>
                )}
              </div>

              {/* Missing Materials Section - Materials in Emersya but not in database */}
              {crossCheckEnabled && typeFormData.technicalName && getMissingMaterials().length > 0 && (
                <div className="ay-mt-4 ay-border ay-border-orange-300 ay-rounded-md ay-p-3 ay-bg-orange-50">
                  <h5 className="ay-text-sm ay-font-medium ay-text-orange-800 ay-mb-3 ay-flex ay-items-center ay-gap-2">
                    <span className="ay-text-lg">⚠️</span>
                    Missing from Database ({getMissingMaterials().length} materials)
                  </h5>
                  <p className="ay-text-xs ay-text-orange-700 ay-mb-3">
                    These materials exist in Emersya but are not in your materials database.
                    Add them to make them selectable in the configurator.
                  </p>
                  <div className="ay-grid ay-grid-cols-2 sm:ay-grid-cols-3 md:ay-grid-cols-4 lg:ay-grid-cols-6 ay-gap-2">
                    {getMissingMaterials().map((variation, index) => (
                      <div
                        key={`missing-${variation.cleanName}-${index}`}
                        className={`ay-border ay-rounded-lg ay-p-2 ay-bg-white ay-shadow-sm ${
                          variation.hasSpacingIssue
                            ? "ay-border-red-400 ay-ring-1 ay-ring-red-200"
                            : "ay-border-orange-400"
                        }`}
                      >
                        <div className="ay-flex ay-flex-col ay-items-center ay-gap-2">
                          <div className={`ay-w-10 ay-h-10 ay-rounded-full ay-border ay-flex ay-items-center ay-justify-center ${
                            variation.hasSpacingIssue
                              ? "ay-bg-red-200 ay-border-red-300"
                              : "ay-bg-orange-200 ay-border-orange-300"
                          }`}>
                            <span className={`ay-text-lg ${variation.hasSpacingIssue ? "ay-text-red-600" : "ay-text-orange-600"}`}>
                              {variation.hasSpacingIssue ? "⚡" : "?"}
                            </span>
                          </div>
                          <p
                            className="ay-text-xs ay-text-center ay-font-medium ay-truncate ay-w-full"
                            title={variation.hasSpacingIssue
                              ? `Original: "${variation.name}" → Will be saved as: "${variation.cleanName}"`
                              : variation.cleanName
                            }
                          >
                            {variation.cleanName}
                          </p>
                          {variation.hasSpacingIssue && (
                            <span className="ay-text-[10px] ay-text-red-600 ay-bg-red-100 ay-px-1 ay-rounded" title="Emersya has spacing issues - will be auto-fixed">
                              spacing fix
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleOpenAddMaterial(variation.cleanName, variation.name)}
                            className="ay-px-2 ay-py-1 ay-bg-orange-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-orange-600 ay-transition-colors"
                          >
                            + Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Add New Material Modal */}
        {showAddMaterialModal && (
          <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-[60]">
            <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-md">
              <h3 className="ay-text-lg ay-font-bold ay-mb-4">
                Add New Material
              </h3>

              <div className="ay-space-y-4">
                {/* Name */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newMaterialData.name}
                    onChange={(e) => setNewMaterialData(prev => ({ ...prev, name: e.target.value }))}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    placeholder="Material name"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Slug (for materials.json) *
                  </label>
                  <input
                    type="text"
                    value={newMaterialData.slug}
                    onChange={(e) => setNewMaterialData(prev => ({ ...prev, slug: e.target.value }))}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-bg-gray-50"
                    placeholder="Clean slug for database"
                  />
                  <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                    Clean name used in materials.json for UI matching
                  </p>
                </div>

                {/* Original Emersya Name - show if different from clean name */}
                {newMaterialData.originalEmersyaName && newMaterialData.originalEmersyaName !== newMaterialData.slug && (
                  <div className="ay-bg-yellow-50 ay-border ay-border-yellow-300 ay-rounded-md ay-p-3">
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-yellow-800 ay-mb-1">
                      Emersya Original Name (for products.json)
                    </label>
                    <code className="ay-block ay-bg-yellow-100 ay-px-2 ay-py-1 ay-rounded ay-text-sm ay-font-mono">
                      "{newMaterialData.originalEmersyaName}"
                    </code>
                    <p className="ay-text-xs ay-text-yellow-700 ay-mt-1">
                      ⚠️ This exact string (with spaces) will be saved to products.json for Emersya API calls
                    </p>
                  </div>
                )}

                {/* Collection */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Collection
                  </label>
                  <select
                    value={newMaterialData.collection}
                    onChange={(e) => setNewMaterialData(prev => ({ ...prev, collection: e.target.value }))}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                  >
                    <option value="">Select collection...</option>
                    {collections.map(collection => (
                      <option key={collection} value={collection}>
                        {collection}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Thumbnail Type Toggle */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
                    Thumbnail
                  </label>
                  <div className="ay-flex ay-gap-4 ay-mb-2">
                    <label className="ay-flex ay-items-center ay-gap-2">
                      <input
                        type="radio"
                        checked={!useColorPicker}
                        onChange={() => setUseColorPicker(false)}
                        className="ay-rounded"
                      />
                      <span className="ay-text-sm">Image Upload</span>
                    </label>
                    <label className="ay-flex ay-items-center ay-gap-2">
                      <input
                        type="radio"
                        checked={useColorPicker}
                        onChange={() => setUseColorPicker(true)}
                        className="ay-rounded"
                      />
                      <span className="ay-text-sm">Solid Color</span>
                    </label>
                  </div>

                  {useColorPicker ? (
                    <div className="ay-flex ay-items-center ay-gap-3">
                      <input
                        type="color"
                        value={newMaterialData.thumbnail.startsWith("#") ? newMaterialData.thumbnail : "#cccccc"}
                        onChange={(e) => setNewMaterialData(prev => ({ ...prev, thumbnail: e.target.value }))}
                        className="ay-w-12 ay-h-12 ay-border ay-border-gray-300 ay-rounded ay-cursor-pointer"
                      />
                      <input
                        type="text"
                        value={newMaterialData.thumbnail}
                        onChange={(e) => setNewMaterialData(prev => ({ ...prev, thumbnail: e.target.value }))}
                        className="ay-flex-grow ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                        placeholder="#ffffff or rgb(255,255,255)"
                      />
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleNewMaterialThumbnailChange}
                        className="ay-block ay-w-full ay-text-sm ay-text-gray-500 ay-file:ay-mr-4 ay-file:ay-py-2 ay-file:ay-px-4 ay-file:ay-rounded ay-file:ay-border-0 ay-file:ay-text-sm ay-file:ay-font-semibold ay-file:ay-bg-blue-50 ay-file:ay-text-blue-700 ay-hover:ay-file:ay-bg-blue-100"
                      />
                      {newMaterialThumbnailPreview && (
                        <div className="ay-mt-2 ay-flex ay-items-center ay-gap-2">
                          <img
                            src={newMaterialThumbnailPreview}
                            alt="Preview"
                            className="ay-w-16 ay-h-16 ay-object-cover ay-rounded ay-border"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setNewMaterialThumbnailFile(null)
                              setNewMaterialThumbnailPreview(null)
                            }}
                            className="ay-text-red-500 ay-text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Technical Type (read-only) */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Technical Type
                  </label>
                  <input
                    type="text"
                    value={newMaterialData.technicalType}
                    readOnly
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-bg-gray-100"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="ay-flex ay-justify-end ay-gap-3 ay-mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddMaterialModal(false)}
                  disabled={isAddingMaterial}
                  className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-text-gray-700 ay-disabled:ay-opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveNewMaterial}
                  disabled={isAddingMaterial || !newMaterialData.name || !newMaterialData.slug}
                  className="ay-px-4 ay-py-2 ay-bg-blue-500 ay-text-white ay-rounded-md ay-disabled:ay-opacity-50 ay-disabled:ay-cursor-not-allowed"
                >
                  {isAddingMaterial ? "Adding..." : "Add Material"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MaterialTypeManager
